import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowRight } from 'lucide-react';
import { useB2BAccess, getModuleInfo } from '@/hooks/useB2BAccess';
import { type B2BModule } from '@/config/plans';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { B2BModuleSelector } from './B2BModuleSelector';

interface B2BModuleGateProps {
  module: B2BModule;
  children: ReactNode;
}

/**
 * Wrapper component that gates access to B2B module pages
 * Shows upgrade prompt if user doesn't have access
 */
export function B2BModuleGate({ module, children }: B2BModuleGateProps) {
  const navigate = useNavigate();
  const { 
    canAccessModule, 
    hasB2BAccess, 
    needsModuleSelection,
    planName,
    isLoading 
  } = useB2BAccess();

  const moduleInfo = getModuleInfo(module);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // User has access - show content
  if (canAccessModule(module)) {
    return <>{children}</>;
  }

  // Pro user needs to select their module
  if (needsModuleSelection && hasB2BAccess) {
    return <B2BModuleSelector targetModule={module} />;
  }

  // No access - show upgrade prompt
  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <Card className="border-dashed">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-4 rounded-full bg-muted">
            <Lock className="h-8 w-8 text-muted-foreground" />
          </div>
          <CardTitle className="text-2xl">
            Module {moduleInfo.name} verrouillé
          </CardTitle>
          <CardDescription className="text-base mt-2">
            {moduleInfo.description}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm text-muted-foreground">
              Vous êtes actuellement sur le plan <strong>{planName}</strong>.
              {!hasB2BAccess 
                ? " Les modules B2B sont disponibles à partir du plan Pro."
                : " Ce module n'est pas inclus dans votre sélection actuelle."}
            </p>
          </div>

          <div className="space-y-3">
            {!hasB2BAccess ? (
              <>
                <Button 
                  onClick={() => navigate('/pricing')} 
                  className="w-full"
                  size="lg"
                >
                  Passer au plan Pro
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  À partir de 29€/mois • 1 module B2B inclus
                </p>
              </>
            ) : (
              <>
                <Button 
                  onClick={() => navigate('/pricing')} 
                  className="w-full"
                  size="lg"
                >
                  Ajouter ce module (+12€/mois)
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  Ou passez au plan Team pour accéder à tous les modules
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
